import React, { useState, useEffect, useCallback } from "react";
import { Song } from "../../types/song";
import { MUSIC_PLATFORMS } from "../../config/platforms";

interface SongsManagerProps {
  adminPassword: string;
}

const MAX_IMAGE_SIZE_BYTES = 500 * 1024; // 500KB — matches the server-side limit in api/upload-song-image.js

type FormState = {
  id: number | null;
  title: string;
  slug: string;
  coverImage: string;
  shortDescription: string;
  story: string;
  lyrics: string;
  releaseDate: string;
  featured: boolean;
  displayOrder: string;
} & Record<string, any>;

const EMPTY_FORM: FormState = {
  id: null,
  title: "",
  slug: "",
  coverImage: "",
  shortDescription: "",
  story: "",
  lyrics: "",
  releaseDate: "",
  featured: false,
  displayOrder: "0",
  spotifyUrl: "",
  youtubeMusicUrl: "",
  youtubeVideoUrl: "",
  appleMusicUrl: "",
  amazonMusicUrl: "",
  jioSaavnUrl: "",
  gaanaUrl: "",
  instagramReelUrl: "",
  heroCtaUrl: "",
  embeddedPlayerUrl: "",
};

function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const SongsManager: React.FC<SongsManagerProps> = ({ adminPassword }) => {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const authHeaders = { Authorization: `Bearer ${adminPassword}` };

  const fetchSongs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/admin-songs", { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setSongs(data.songs || []);
      }
    } catch (err) {
      console.error("Error fetching songs:", err);
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => {
    fetchSongs();
  }, [fetchSongs]);

  const openCreateForm = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (song: Song) => {
    setForm({
      id: song.id,
      title: song.title,
      slug: song.slug,
      coverImage: song.coverImage || "",
      shortDescription: song.shortDescription || "",
      story: song.story || "",
      lyrics: song.lyrics || "",
      releaseDate: song.releaseDate ? song.releaseDate.slice(0, 10) : "",
      featured: song.featured,
      displayOrder: String(song.displayOrder ?? 0),
      spotifyUrl: song.spotifyUrl || "",
      youtubeMusicUrl: song.youtubeMusicUrl || "",
      youtubeVideoUrl: song.youtubeVideoUrl || "",
      appleMusicUrl: song.appleMusicUrl || "",
      amazonMusicUrl: song.amazonMusicUrl || "",
      jioSaavnUrl: song.jioSaavnUrl || "",
      gaanaUrl: song.gaanaUrl || "",
      instagramReelUrl: song.instagramReelUrl || "",
      heroCtaUrl: song.heroCtaUrl || "",
      embeddedPlayerUrl: song.embeddedPlayerUrl || "",
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleTitleChange = (title: string) => {
    setForm((prev) => ({
      ...prev,
      title,
      // Slug is frozen once a song exists (id set) so shared /songs/:slug links never break
      slug: prev.id ? prev.slug : slugify(title),
    }));
  };

  const handleImageUpload = async (file: File) => {
    setFormError(null);

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setFormError("Album artwork must be 500 KB or smaller.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch("/api/upload-song-image", {
        method: "POST",
        headers: authHeaders, // no Content-Type — browser sets the multipart boundary
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        setForm((prev) => ({ ...prev, coverImage: data.url }));
      } else {
        setFormError(data.error || "Image upload failed");
      }
    } catch (err) {
      setFormError("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) {
      setFormError("Title and slug are required");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        ...form,
        displayOrder: parseInt(form.displayOrder, 10) || 0,
        releaseDate: form.releaseDate || null,
      };

      const response = await fetch("/api/admin-songs", {
        method: form.id ? "PUT" : "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        setShowForm(false);
        await fetchSongs();
      } else if (response.status === 409) {
        setFormError("A song with that slug already exists");
      } else {
        setFormError(data.error || "Failed to save song");
      }
    } catch (err) {
      setFormError("Failed to save song");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (song: Song) => {
    if (!window.confirm(`Delete "${song.title}"? This cannot be undone.`)) return;
    try {
      const response = await fetch(`/api/admin-songs?id=${song.id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (response.ok) {
        await fetchSongs();
      } else {
        alert("Failed to delete song");
      }
    } catch (err) {
      alert("Failed to delete song");
    }
  };

  const handleMakeFeatured = async (song: Song) => {
    if (!window.confirm(`Make "${song.title}" the featured song? This will replace what /listen currently shows.`)) return;
    try {
      const response = await fetch("/api/admin-songs", {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ ...song, releaseDate: song.releaseDate, featured: true }),
      });
      if (response.ok) {
        await fetchSongs();
      } else {
        alert("Failed to set featured song");
      }
    } catch (err) {
      alert("Failed to set featured song");
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">🎵 Songs</h2>
          <p className="text-gray-600 mt-1 text-sm">
            /listen always shows the featured song. Changes appear there within ~5 minutes.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchSongs}
            disabled={loading}
            className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition disabled:opacity-50"
          >
            {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
          </button>
          <button
            onClick={openCreateForm}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-2 rounded-lg hover:opacity-90 transition"
          >
            + New Song
          </button>
        </div>
      </div>

      {!showForm && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-200 text-sm text-gray-500">
                <th className="py-2 pr-4">Artwork</th>
                <th className="py-2 pr-4">Title</th>
                <th className="py-2 pr-4">Slug</th>
                <th className="py-2 pr-4">Release Date</th>
                <th className="py-2 pr-4">Featured</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {songs.map((song) => (
                <tr key={song.id} className="border-b border-gray-100">
                  <td className="py-3 pr-4">
                    {song.coverImage ? (
                      <img src={song.coverImage} alt={song.title} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gray-100" />
                    )}
                  </td>
                  <td className="py-3 pr-4 font-semibold text-gray-800">{song.title}</td>
                  <td className="py-3 pr-4 text-gray-500 text-sm">{song.slug}</td>
                  <td className="py-3 pr-4 text-gray-500 text-sm">{song.releaseDate ? song.releaseDate.slice(0, 10) : "—"}</td>
                  <td className="py-3 pr-4">{song.featured ? "⭐" : ""}</td>
                  <td className="py-3 pr-4">
                    <div className="flex gap-2">
                      <button onClick={() => openEditForm(song)} className="text-purple-600 hover:underline text-sm font-semibold">
                        Edit
                      </button>
                      {!song.featured && (
                        <button onClick={() => handleMakeFeatured(song)} className="text-indigo-600 hover:underline text-sm font-semibold">
                          Make Featured
                        </button>
                      )}
                      <button onClick={() => handleDelete(song)} className="text-red-500 hover:underline text-sm font-semibold">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {songs.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400">
                    No songs yet — create one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="space-y-6 max-w-2xl">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{formError}</div>
          )}

          {/* Artwork */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Album Artwork</label>
            <div className="flex items-center gap-4">
              {form.coverImage ? (
                <img src={form.coverImage} alt="Cover preview" className="w-20 h-20 rounded-lg object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-gray-100" />
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                disabled={uploading}
              />
              {uploading && <span className="text-sm text-gray-500">Uploading...</span>}
            </div>
            <p className="text-xs text-gray-500 mt-1">JPEG, PNG, or WEBP. Max 500 KB.</p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Song Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Slug * {form.id && <span className="text-gray-400 font-normal">(locked after first save)</span>}
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              disabled={!!form.id}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>

          {/* Release Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Release Date</label>
            <input
              type="date"
              value={form.releaseDate}
              onChange={(e) => setForm((prev) => ({ ...prev, releaseDate: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Short description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Short Description</label>
            <input
              type="text"
              value={form.shortDescription}
              onChange={(e) => setForm((prev) => ({ ...prev, shortDescription: e.target.value }))}
              placeholder="Used for search engine and social share descriptions"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Embedded player */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Embedded Player URL (optional)</label>
            <input
              type="url"
              value={form.embeddedPlayerUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, embeddedPlayerUrl: e.target.value }))}
              placeholder="e.g. https://www.youtube.com/embed/VIDEO_ID"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Paste a ready-to-embed URL (usually a YouTube embed link — from Share → Embed → copy the src). Leave blank to hide the player section.
            </p>
          </div>

          {/* Streaming links */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Streaming Links</label>
            <div className="space-y-2">
              {MUSIC_PLATFORMS.map((platform) => (
                <input
                  key={platform.id}
                  type="url"
                  value={form[platform.urlKey] || ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, [platform.urlKey]: e.target.value }))}
                  placeholder={`${platform.name} URL`}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              ))}
              <input
                type="url"
                value={form.youtubeVideoUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, youtubeVideoUrl: e.target.value }))}
                placeholder="YouTube Video URL (music video)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <input
                type="url"
                value={form.instagramReelUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, instagramReelUrl: e.target.value }))}
                placeholder="Instagram Reel URL"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Hero CTA override */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Hero Button Link (optional override)</label>
            <input
              type="url"
              value={form.heroCtaUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, heroCtaUrl: e.target.value }))}
              placeholder="Leave blank to auto-select"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              The "Listen Now" button uses this link if set. Otherwise it falls back to the first available platform above, in priority order:{" "}
              {MUSIC_PLATFORMS.map((p) => p.name).join(" → ")}.
            </p>
          </div>

          {/* Story */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Behind the Song
              <span className="text-gray-400 font-normal ml-1">(supports ##, **bold**, *italic*, - lists)</span>
            </label>
            <textarea
              value={form.story}
              onChange={(e) => setForm((prev) => ({ ...prev, story: e.target.value }))}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Lyrics */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Lyrics (optional)</label>
            <textarea
              value={form.lyrics}
              onChange={(e) => setForm((prev) => ({ ...prev, lyrics: e.target.value }))}
              rows={8}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Featured */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm((prev) => ({ ...prev, featured: e.target.checked }))}
              />
              <span className="text-sm font-medium text-gray-700">Featured (shown on /listen)</span>
            </label>
            {form.featured && (
              <p className="text-sm text-amber-600 mt-1">
                This will un-feature the current featured song and change what /listen shows.
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving || uploading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Song"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongsManager;
